
import { useEffect, useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { CheckCheck, Trash2, Loader2 } from "lucide-react"
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  subscribeToNotifications,
  unsubscribeFromNotifications,
  getNotificationIcon,
  formatNotificationTime,
  type Notification
} from "@/lib/services/notifications"
import { useAuth } from "@/lib/auth/auth-context"
import { toast } from "sonner"
import type { RealtimeChannel } from '@supabase/supabase-js'

interface NotificationPanelProps {
  onUnreadCountChange?: (count: number) => void
}

export function NotificationPanel({ onUnreadCountChange }: NotificationPanelProps) {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [markingAllRead, setMarkingAllRead] = useState(false)

  // Load notifications
  useEffect(() => {
    if (!user?.id) {
      setNotifications([])
      setLoading(false)
      return
    }

    const loadNotifications = async () => {
      try {
        setLoading(true)
        const data = await fetchNotifications(user.id)
        setNotifications(data)

        // Update unread count
        const unreadCount = data.filter(n => !n.read).length
        onUnreadCountChange?.(unreadCount)
      } catch (error) {
        console.error('Error loading notifications:', error)
        toast.error('Failed to load notifications')
      } finally {
        setLoading(false)
      }
    }

    loadNotifications()
  }, [user?.id, onUnreadCountChange])

  // Subscribe to real-time updates
  useEffect(() => {
    if (!user?.id) return

    let channel: RealtimeChannel

    const setupSubscription = async () => {
      channel = subscribeToNotifications(
        user.id,
        // onNotification (INSERT)
        (newNotification) => {
          setNotifications(prev => [newNotification, ...prev])
          const unreadCount = [newNotification, ...notifications].filter(n => !n.read).length
          onUnreadCountChange?.(unreadCount)

          // Show toast for new notification
          toast.info(newNotification.title, {
            description: newNotification.message,
            icon: getNotificationIcon(newNotification.type),
          })
        },
        // onDelete
        (notificationId) => {
          setNotifications(prev => {
            const updated = prev.filter(n => n.id !== notificationId)
            const unreadCount = updated.filter(n => !n.read).length
            onUnreadCountChange?.(unreadCount)
            return updated
          })
        },
        // onUpdate
        (updatedNotification) => {
          setNotifications(prev => {
            const updated = prev.map(n =>
              n.id === updatedNotification.id ? updatedNotification : n
            )
            const unreadCount = updated.filter(n => !n.read).length
            onUnreadCountChange?.(unreadCount)
            return updated
          })
        }
      )
    }

    setupSubscription()

    return () => {
      if (channel) {
        unsubscribeFromNotifications(channel)
      }
    }
  }, [user?.id, onUnreadCountChange, notifications])

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId)

      // Optimistic update
      setNotifications(prev => {
        const updated = prev.map(n =>
          n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n
        )
        const unreadCount = updated.filter(n => !n.read).length
        onUnreadCountChange?.(unreadCount)
        return updated
      })
    } catch (error) {
      console.error('Error marking notification as read:', error)
      toast.error('Failed to mark notification as read')
    }
  }

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return

    try {
      setMarkingAllRead(true)
      await markAllNotificationsAsRead(user.id)

      // Update all notifications to read
      setNotifications(prev => {
        const now = new Date().toISOString()
        return prev.map(n => ({ ...n, read: true, read_at: now }))
      })

      onUnreadCountChange?.(0)
      toast.success('All notifications marked as read')
    } catch (error) {
      console.error('Error marking all as read:', error)
      toast.error('Failed to mark all as read')
    } finally {
      setMarkingAllRead(false)
    }
  }

  const handleDelete = async (notificationId: string) => {
    try {
      await deleteNotification(notificationId)

      // Optimistic update
      setNotifications(prev => {
        const updated = prev.filter(n => n.id !== notificationId)
        const unreadCount = updated.filter(n => !n.read).length
        onUnreadCountChange?.(unreadCount)
        return updated
      })

      toast.success('Notification deleted')
    } catch (error) {
      console.error('Error deleting notification:', error)
      toast.error('Failed to delete notification')
    }
  }

  const unreadNotifications = notifications.filter(n => !n.read)

  return (
    <SheetContent side="right" className="w-full sm:max-w-md p-0">
      <SheetHeader className="px-6 py-4 border-b">
        <SheetTitle>Notifications</SheetTitle>
        <SheetDescription>
          {unreadNotifications.length > 0
            ? `You have ${unreadNotifications.length} unread notification${unreadNotifications.length === 1 ? '' : 's'}`
            : 'No new notifications'}
        </SheetDescription>
      </SheetHeader>

      {/* Mark all as read button */}
      {unreadNotifications.length > 0 && (
        <div className="px-6 py-3 border-b">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkAllAsRead}
            disabled={markingAllRead}
            className="w-full"
          >
            {markingAllRead ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Marking all as read...
              </>
            ) : (
              <>
                <CheckCheck className="mr-2 h-4 w-4" />
                Mark all as read
              </>
            )}
          </Button>
        </div>
      )}

      {/* Notifications list */}
      <ScrollArea className="flex-1 h-[calc(100vh-180px)]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <span className="text-3xl">🔔</span>
            </div>
            <p className="text-center text-muted-foreground">
              No notifications yet
            </p>
            <p className="text-center text-sm text-muted-foreground mt-1">
              We'll notify you about important updates
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`px-6 py-4 hover:bg-muted/50 transition-colors ${
                  !notification.read ? 'bg-primary/5' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center text-xl shrink-0 ${
                    !notification.read ? 'bg-primary/10' : 'bg-muted'
                  }`}>
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className={`font-medium text-sm ${
                        !notification.read ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {notification.title}
                      </h4>
                      {!notification.read && (
                        <div className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {formatNotificationTime(notification.created_at)}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="h-8 text-xs"
                        >
                          Mark as read
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(notification.id)}
                        className="h-8 text-xs text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </SheetContent>
  )
}
